import { useRef, useState } from "react";
import { toBlob } from "html-to-image";
import { Share2, Loader2, Send } from "lucide-react";
import { useLang } from "@/context/LangContext";

// Renders `card` off-screen on demand, captures it as PNG and shares it (native share sheet, else download + WhatsApp link)
export default function ShareImageButton({ card, fileName, text, url, testid = "share-image-btn", className = "" }) {
  const { t, lang } = useLang();
  const ta = lang === "ta";
  const ref = useRef(null);
  const [rendering, setRendering] = useState(false);
  const [status, setStatus] = useState("");
  const [ready, setReady] = useState(null);

  async function send(file) {
    try {
      await navigator.share({ files: [file], text: `${text} ${url}`, title: "NeeYosi" });
      setStatus("");
      setReady(null);
    } catch (e) {
      if (e?.name === "NotAllowedError") { setReady(file); setStatus(""); return; }
      if (e?.name !== "AbortError") fallback(file);
    }
  }

  function fallback(file) {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(file);
    a.download = file.name;
    a.click();
    window.open(`https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`, "_blank", "noopener");
    setStatus(t("share_downloaded"));
  }

  async function handleShare() {
    setStatus(t("share_preparing"));
    setRendering(true);
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
    try {
      await document.fonts.ready;
      const blob = await toBlob(ref.current, { pixelRatio: 2, cacheBust: true });
      const file = new File([blob], fileName, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) await send(file);
      else fallback(file);
    } catch {
      setStatus(t("share_failed"));
    } finally {
      setRendering(false);
    }
  }

  return (
    <div className={className}>
      <button
        onClick={ready ? () => send(ready) : handleShare}
        disabled={rendering}
        className={`inline-flex items-center gap-2 rounded-full bg-[#25d366] px-4 py-2 text-[13px] font-semibold text-[#0b3d1e] hover:bg-[#1fbe5b] active:scale-[0.98] transition-transform disabled:opacity-70 ${ta ? "font-tamil" : ""}`}
        data-testid={testid}
      >
        {rendering ? <Loader2 size={15} className="animate-spin" /> : ready ? <Send size={15} /> : <Share2 size={15} />}
        {t("share_seat")}
      </button>
      {status && <p className={`mt-2 text-[12px] text-[#6b6894] ${ta ? "font-tamil" : ""}`} data-testid={`${testid}-status`}>{status}</p>}
      {rendering && (
        <div style={{ position: "fixed", left: -10000, top: 0, pointerEvents: "none" }} aria-hidden>
          <div ref={ref}>{card}</div>
        </div>
      )}
    </div>
  );
}
